import { asNumber, asString } from './raw.ts';

/**
 * R3 forbids `Date` in models and R1 forbids it in core at all, so ISO-8601 is converted by hand.
 * This is Howard Hinnant's days-from-civil algorithm: pure integer arithmetic, no library, and it
 * ports to Kotlin unchanged.
 */
function daysFromCivil(year: number, month: number, day: number): number {
  const y = year - (month <= 2 ? 1 : 0);
  const era = Math.floor(y / 400);
  const yearOfEra = y - era * 400;
  const dayOfYear = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
  return era * 146097 + dayOfEra - 719468;
}

const ISO_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|z|[+-]\d{2}:?\d{2})?$/;

/**
 * Strict on purpose: anything that is not the shape the API actually emits returns `null` rather
 * than a plausible-looking wrong number. A missing offset is treated as UTC — the API always sends `Z`.
 */
export function parseIso8601ToEpochSec(input: string): number | null {
  const match = ISO_RE.exec(input.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = match[6] === undefined ? 0 : Number(match[6]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (hour > 23 || minute > 59 || second > 60) return null;

  let offsetSec = 0;
  const offset = match[7];
  if (offset !== undefined && offset !== 'Z' && offset !== 'z') {
    const sign = offset.startsWith('-') ? -1 : 1;
    const digits = offset.slice(1).replace(':', '');
    const offsetHours = Number(digits.slice(0, 2));
    const offsetMinutes = Number(digits.slice(2, 4));
    if (!Number.isFinite(offsetHours) || !Number.isFinite(offsetMinutes)) return null;
    offsetSec = sign * (offsetHours * 3600 + offsetMinutes * 60);
  }

  return daysFromCivil(year, month, day) * 86400 + hour * 3600 + minute * 60 + second - offsetSec;
}

/**
 * `published` is epoch **seconds** at the top level of `/api/v1/videos/{id}` but an **ISO-8601
 * string** inside that same response's `recommendedVideos[]`. Verified on `dQw4w9WgXcQ`:
 * `published: 1256342400` alongside `recommendedVideos[0].published: "2026-07-27T08:07:48Z"`.
 * One helper, both shapes.
 */
export function asEpochSeconds(value: unknown): number | null {
  const numeric = asNumber(value);
  if (numeric !== null) {
    if (numeric <= 0) return null;
    // Tolerate a millisecond timestamp arriving where seconds were promised.
    return numeric > 100_000_000_000 ? Math.floor(numeric / 1000) : Math.floor(numeric);
  }
  const text = asString(value);
  if (text === null) return null;
  if (/^\d+$/.test(text.trim())) {
    const parsed = Number(text.trim());
    return parsed > 0 ? parsed : null;
  }
  return parseIso8601ToEpochSec(text);
}
